import { useEffect, useMemo, useRef } from "react";
import "./properties.component.scss";
import { Assets, Colors, FontFamily, Input, PrimaryButton, RenameInput, UploadFileCard, ColorPicker, Dropdown, TransformProperties } from "utils/imports.utils";
import { ButtonSize, ButtonStyles, ButtonTypes, ColorPickerTypes, FontFamilyTypes, FontStyles, ObjectTypes, PropertiesTypes, UploadFileTypes, IObject, IDropdownData, TargetImageObject, ProjectStatus } from "utils/interface.utils";
import { compareObjectProperties, convertImageUrlToFile, findButtonPresetByStyles, getDefaultStateForObjectType, getObjectDisplayName, getObjectTypeLabel, getTargetDisplayName, normalizeEntityName, ENTITY_NAME_MAX_LENGTH, isNameTaken, useSetState } from "utils/functions.utils";
import { STRINGS } from "constants/strings.constant";
import { compileFiles } from "utils/compiler";
import { TARGET_IMAGE_DIMENSIONS_CANVAS, FONT_SIZE_MIN, FONT_SIZE_MAX } from "utils/constant.utils";
import { OpenSansFF, FunnelSans, IBMPlexSerifFF, PoppinsFF, RobotoFF, SpectralFF, RubikFF, TinosFF } from "utils/fonts.utils";
import { shallowEqual, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "store/store";
import { useSelector } from "react-redux";
import { updateTargetImage, addObjectsToTargetImage, updateObjectInTargetImage, setShouldUpdateObjectState, setIsUpdatingFromEditorWorkspace, setCurrentSelectedPropertyType, setSelectedObjectID, renameTargetImage, renameObjectInTargetImage } from "store/reducers/editorSlice";

interface IProperties {
    projectPaymentStatus: boolean,
    projectStatus: string,
    // Bumped by the editor's empty-state card to ask for the target upload dialog again.
    uploadRequestCount?: number,
}

const Properties = (props: IProperties) => {
    const dispatch = useDispatch<AppDispatch>();

    const targetImageDataList = useSelector((state: RootState) => state.editor.targetImageDataList, shallowEqual)
    const { currentSelectedPropertyType, selectedTargetImageID, selectedTargetIndex, selectedObjectID, isUpdatingFromEditorWorkspace, isTargetImageListItemClicked, shouldUpdateObjectState } = useSelector((state: RootState) => state.editor)
    const currentProject = useSelector((state: RootState) => state.project.currentProject)

    const _isPersist = useSelector((state: RootState) => state.editor._persist);

    const [state, setState] = useSetState({
        renameDraft: null as string | null,
        fileUploadType: null,
        isFileUploadModalOpen: false,
        selectedFontFamily: FontFamilyTypes.MONTSERRAT,
        selectedFontStyle: FontStyles.BOLD,
        uploadedVideo: null,
        listOfAddedObjectsIDs: [],
        // Object Image
        [ObjectTypes.IMAGE]: {
            src: null,
            positionX: 0,
            positionY: 0,
            rotationX: 0,
            scaleX: 1,
            scaleY: 1,
            renderOrder: 0,
        },
        [ObjectTypes.VIDEO]: {
            src: null,
            positionX: 0,
            positionY: 0,
            rotationX: 0,
            scaleX: 1,
            scaleY: 1,
            renderOrder: 0,
        },
        [ObjectTypes.BUTTON]: {
            presets: {
                name: ButtonStyles.PRIMARY_WITH_BORDER_RADIUS,
                styles: {
                    inActive: { backgroundColor: Colors.fontPrimary, borderRadius: "8px", color: Colors.white },
                    active: { backgroundColor: Colors.primaryColor, borderRadius: "8px", color: Colors.onPrimary },
                    passed: { backgroundColor: Colors.fontPrimary, borderRadius: 8, color: Colors.white }
                }
            },
            selectedPreset: { backgroundColor: Colors.fontPrimary, borderRadius: 8, color: Colors.white },
            fontFamily: FontFamilyTypes.MONTSERRAT,
            url: "",
            text: "Text",
            fontSize: { label: "12", value: "12" },
            fontStyle: FontStyles.BOLD,
            fontColor: Colors.white,
            bgColor: "#484848",
            positionX: 0,
            positionY: 0,
            rotationX: 0,
            scaleX: 1,
            scaleY: 1,
            renderOrder: 0,
        },
        [ObjectTypes.TEXT]: {
            text: "Mushroom villa",
            fontSize: { label: "48", value: "48" },
            fontFamily: FontFamilyTypes.MONTSERRAT,
            fontStyle: FontStyles.BOLD,
            fontColor: Colors.black,
            bgColor: Colors.transparent,
            positionX: 0,
            positionY: 0,
            rotationX: 0,
            scaleX: 1,
            scaleY: 1,
            renderOrder: 0,
        },
        [ObjectTypes.SOCIAL]: {
            assetSrc: Assets.social_yt,
            url: "",
            color: "#FF0000",
            bgColor: Colors.white,
            positionX: 0,
            positionY: 0,
            rotationX: 0,
            scaleX: TARGET_IMAGE_DIMENSIONS_CANVAS * 0.05,
            scaleY: TARGET_IMAGE_DIMENSIONS_CANVAS * 0.05,
            renderOrder: 0,
        }
    })

    const stateRef = useRef(state);

    // Selecting Image/Video from the Choose Your Object card points the panel at a brand-new
    // object ID that isn't added to Redux until an upload actually succeeds — tracks the last
    // selection that pointed at a real, already-created object (or the target slot itself), so
    // cancelling the auto-opened upload modal for that not-yet-created placeholder can revert to
    // it instead of leaving the panel stuck on the phantom placeholder.
    const lastValidSelectionRef = useRef<{ objectID: string | null, type: PropertiesTypes | null }>({ objectID: null, type: null });

    // Set when the panel has just re-read its form state from the store, so the sync-back effect
    // below — which fires on any state.<type> change — can tell "the author edited a field" from
    // "the store changed underneath us and we caught up". Hydration is a read; writing it back
    // would push a redundant history snapshot and clear the redo stack, which after an undo means
    // the undone step is immediately re-committed and redo is destroyed.
    const hydratedFromStoreRef = useRef<boolean>(false);

    // const isUpdatingFromEditorWorkspace = useRef<boolean>(false);

    // Check if the current target can be replaced by checking,
    // 1. The properties type is target.
    // 2. The selectedTargetIndex(Current Target Index number) is not null.
    // 3. The targetImageDataList at the selectedTargetIndex has a targetImage.
    // If all the above conditions are satisfied then the current target image can be replaced.
    const IS_CURRENT_TARGET_CAN_BE_REPLACED = useMemo(() => {
        return !!(currentSelectedPropertyType === PropertiesTypes.TARGET && selectedTargetIndex !== null && targetImageDataList[selectedTargetIndex ?? 0]?.targetImage)
    }, [currentSelectedPropertyType, selectedTargetIndex, targetImageDataList])

    // The Controls section (Position / Rotation / Scale) transforms an object, so it only has
    // something to act on once an object is the selection -- not for the target image itself
    // (nothing added on it yet) and not while nothing is selected.
    const IS_OBJECT_SELECTED = currentSelectedPropertyType !== null && currentSelectedPropertyType !== PropertiesTypes.TARGET;

    // PAYMENT.md Phase 4 -- once a target is paid, its image/video content is frozen server-side
    // immediately (no grace window); this mirrors that here so the upload UI never lets the user
    // attempt a swap the server will reject anyway.
    const IS_CURRENT_TARGET_PAID = !!targetImageDataList[selectedTargetIndex ?? 0]?.isLocked;
    // Only the target image stays locked once paid (changing it would invalidate the compiled target). Image and video
    // objects can be deleted, added and replaced; the server's one-video-per-target limit still applies on upload.
    const IS_TARGET_LOCKED = IS_CURRENT_TARGET_PAID && currentSelectedPropertyType === PropertiesTypes.TARGET;



    const listOfFontSizes: Array<IDropdownData> = [
        { label: "12", value: "12" },
        { label: "14", value: "14" },
        { label: "16", value: "16" },
        { label: "18", value: "18" },
        { label: "20", value: "20" },
        { label: "22", value: "22" },
        { label: "24", value: "24" },
        { label: "26", value: "26" },
        { label: "28", value: "28" },
        { label: "30", value: "30" },
        { label: "32", value: "32" },
        { label: "34", value: "34" },
        { label: "36", value: "36" },
        { label: "38", value: "38" },
        { label: "40", value: "40" },
        { label: "42", value: "42" },
        { label: "44", value: "44" },
        { label: "46", value: "46" },
        { label: "48", value: "48" },
        { label: "50", value: "50" },
        { label: "52", value: "52" },
        { label: "54", value: "54" },
        { label: "56", value: "56" },
        { label: "58", value: "58" },
        { label: "60", value: "60" },
        { label: "64", value: "64" },
        { label: "68", value: "68" },
        { label: "72", value: "72" },
        { label: "76", value: "76" },
        { label: "80", value: "80" },
        { label: "84", value: "84" },
        { label: "88", value: "88" },
        { label: "92", value: "92" },
        { label: "96", value: "96" },
        { label: "100", value: "100" },
        { label: "104", value: "104" },
        { label: "108", value: "108" },
        { label: "112", value: "112" },
        { label: "116", value: "116" },
        { label: "120", value: "120" },
    ]

    const fontStyles = [
        { name: FontStyles.BOLD, icon: Assets.bold },
        { name: FontStyles.ITALIC, icon: Assets.italic },
        { name: FontStyles.UNDERLINE, icon: Assets.underline },
    ]

    const handleFontStyleClick = (fontStyleName: string) => {
        setState({ [currentSelectedPropertyType ?? ""]: { ...state[currentSelectedPropertyType ?? ""], fontStyle: fontStyleName }, selectedFontStyle: fontStyleName })
        console.log("%c state", "color: deeppink; font-weight: bold;", state)
    }

    const buttonPresets = [
        {
            name: ButtonStyles.PRIMARY_WITH_BORDER_RADIUS,
            styles: {
                inActive: { backgroundColor: Colors.fontPrimary, borderRadius: "8px", color: Colors.white },
                active: { backgroundColor: Colors.primaryColor, borderRadius: "8px", color: Colors.onPrimary },
                passed: { backgroundColor: Colors.fontPrimary, borderRadius: 8, color: Colors.white }
            }
        },
        {
            name: ButtonStyles.TEXT_WITH_UNDERLINE,
            styles: {
                inActive: { border: "none", borderRadius: "0px", backgroundColor: Colors.transparent, color: Colors.fontPrimary, textDecoration: "underline" },
                active: { border: "none", borderRadius: "0px", backgroundColor: Colors.transparent, color: Colors.primaryTextColor, textDecoration: "underline" },
                passed: { border: "none", borderRadius: 0, backgroundColor: Colors.transparent, color: Colors.fontPrimary, textDecoration: "underline" }
            }
        },
        {
            name: ButtonStyles.OUTLINED_WITH_BORDER_RADIUS,
            styles: {
                inActive: { backgroundColor: Colors.white, borderWidth: "1px", borderStyle: "solid", borderColor: Colors.fontPrimary, borderRadius: "8px", color: Colors.fontPrimary },
                active: { backgroundColor: Colors.white, borderWidth: "1px", borderStyle: "solid", borderColor: Colors.primaryColor, borderRadius: "8px", color: Colors.primaryTextColor },
                passed: { backgroundColor: Colors.white, borderWidth: 1, borderStyle: "solid", borderColor: Colors.fontPrimary, borderRadius: 8, color: Colors.fontPrimary }
            }
        },
        {
            name: ButtonStyles.PRIMARY_WITHOUT_BORDER_RADIUS,
            styles: {
                inActive: { backgroundColor: Colors.fontPrimary, color: Colors.white, borderRadius: "0px" },
                active: { backgroundColor: Colors.primaryColor, color: Colors.onPrimary, borderRadius: "0px" },
                passed: { backgroundColor: Colors.fontPrimary, color: Colors.white, borderRadius: 0 }
            }
        },
        {
            name: ButtonStyles.SECONDARY_WITH_BORDER_RADIUS,
            styles: {
                inActive: { backgroundColor: Colors.secondaryBtnColor, borderRadius: "8px", color: Colors.fontPrimary },
                active: { backgroundColor: Colors.secondaryBtnColor, borderRadius: "8px", color: Colors.primaryTextColor },
                passed: { backgroundColor: Colors.secondaryBtnColor, borderRadius: 8, color: Colors.fontPrimary }
            }
        },
        {
            name: ButtonStyles.OUTLINED_WITHOUT_BORDER_RADIUS,
            styles: {
                inActive: { backgroundColor: Colors.white, borderWidth: "1px", borderStyle: "solid", borderColor: Colors.fontPrimary, color: Colors.fontPrimary, borderRadius: "0px" },
                active: { backgroundColor: Colors.white, borderWidth: "1px", borderStyle: "solid", borderColor: Colors.primaryColor, color: Colors.primaryTextColor, borderRadius: "0px" },
                passed: { backgroundColor: Colors.white, borderWidth: 1, borderStyle: "solid", borderColor: Colors.fontPrimary, color: Colors.fontPrimary, borderRadius: 0 }
            }
        },
    ]

    const handleButtonStyleClick = (style: { name: string, styles: { inActive: IObject, active: IObject, passed: IObject } }) => {
        // The preset's two colours have to be written into fontColor/bgColor, not just carried on
        // `presets`. Both renderers -- renderObjectButton in the editor and renderObjectButtonAR in
        // the published experience -- resolve what they paint as `fontColor || presets?.color` and
        // `bgColor || presets?.backgroundColor`, so the object's own colours win and the preset is
        // only ever a fallback. Those two fields are always populated from the button defaults
        // (#FFFFFF / #484848) the moment the object is created, so that fallback is unreachable:
        // leaving them untouched here meant a preset's colours were dead fields in every state, and
        // picking a preset visibly changed only the corners, border and underline.
        setState({
            [ObjectTypes.BUTTON]: {
                ...state[ObjectTypes.BUTTON],
                presets: style,
                selectedPreset: style.styles.passed,
                fontColor: style.styles.passed.color,
                bgColor: style.styles.passed.backgroundColor,
            }
        })
    }

    const fontFamiliesPresets = [
        { name: FontFamilyTypes.MONTSERRAT, fontFamily: FontFamily.regular },
        { name: FontFamilyTypes.TINOS, fontFamily: TinosFF.regular },
        { name: FontFamilyTypes.OPEN_SANS, fontFamily: OpenSansFF.regular },
        { name: FontFamilyTypes.FUNNEL_SANS, fontFamily: FunnelSans.regular },
        { name: FontFamilyTypes.RUBIK, fontFamily: RubikFF.regular },
        { name: FontFamilyTypes.IBM_PLEX_SERIF, fontFamily: IBMPlexSerifFF.regular },
        { name: FontFamilyTypes.SPECTRAL, fontFamily: SpectralFF.regular },
        { name: FontFamilyTypes.ROBOTO, fontFamily: RobotoFF.regular },
        { name: FontFamilyTypes.POPPINS, fontFamily: PoppinsFF.regular }
    ];

    const handleFontFamilyClick = (fontFamilyName: string) => {
        setState({ selectedFontFamily: fontFamilyName, [currentSelectedPropertyType ?? ""]: { ...state[currentSelectedPropertyType ?? ""], fontFamily: fontFamilyName } })
    };

    const socialIcons = [
        { name: "Youtube", src: Assets.social_yt, activeSrc: Assets.social_yt_active, color: "#FF0000" },
        { name: "Instagram", src: Assets.social_insta, activeSrc: Assets.social_insta_active, color: "#E4405F" },
        { name: "Snapchat", src: Assets.social_snap, activeSrc: Assets.social_snap_active, color: "#FFFC00" },
        { name: "Behance", src: Assets.social_behance, activeSrc: Assets.social_behance_active, color: "#1769FF" },
        { name: "Google", src: Assets.social_google, activeSrc: Assets.social_google_active, color: "#4285F4" },
        { name: "Dropbox", src: Assets.social_dropbox, activeSrc: Assets.social_dropbox_active, color: "#0061FF" },
        { name: "X", src: Assets.social_x, activeSrc: Assets.social_x_active, color: "#000000" },
        { name: "Figma", src: Assets.social_figma, activeSrc: Assets.social_figma_active, color: "#F24E1E" },
    ]

    const handleSocialIconClick = (iconAssetSrc: string, iconColor: string) => {
        setState({ [ObjectTypes.SOCIAL]: { ...state[ObjectTypes.SOCIAL], assetSrc: iconAssetSrc, color: iconColor } })
    }

    const handleFileUploadModalOpenImage = () => {
        // The greyed "Image Locked" button already says why; a paid project offers no way to add another target anyway.
        if (IS_TARGET_LOCKED) return;
        setState({
            isFileUploadModalOpen: true,
            fileUploadType: UploadFileTypes.IMAGE,
        })
    }

    const handleFileUploadModalOpenVideo = () => {
        setState({
            isFileUploadModalOpen: true,
            fileUploadType: UploadFileTypes.VIDEO,
        })
    }

    const uploadTargetAndCompiledTargetImage = (file: { image: string, targetImageDimension: { width: number, height: number } } | { image: string, imageDimension: { width: number, height: number } }) => {
        if (typeof file === 'object' && 'image' in file && 'targetImageDimension' in file) {
            setState({
                isFileUploadModalOpen: false,
            })
            handleUpdateTargetImage(file.image, file.targetImageDimension)
        }
    }

    const handleUpdateTargetImage = (image: string, targetImageDimension: { width: number, height: number }) => {
        dispatch(updateTargetImage({
            id: selectedTargetImageID ?? "",
            targetImage: image,
            targetId: null,
            selectedTargetImageDimension: { width: targetImageDimension.width, height: targetImageDimension.height },
            containedTargetImageDimension: { width: 0, height: 0 },
            objects: targetImageDataList[selectedTargetIndex ?? 0]?.objects || [],
        }))
    }

    const ensureTargetId = async (): Promise<string> => {
        const currentTargetImage = targetImageDataList[selectedTargetIndex ?? 0];
        if (currentTargetImage.targetId) {
            return currentTargetImage.targetId;
        }
        const file = await convertImageUrlToFile(currentTargetImage.targetImage);
        const manifests = await compileFiles([file], [currentTargetImage.id], currentProject?.project_id ?? "", () => {});
        const targetId = manifests[0].target_id;
        dispatch(updateTargetImage({
            ...currentTargetImage,
            targetId,
        }));
        return targetId;
    }

    const handleUploadObjectImage = (file: { image: string, targetImageDimension: { width: number, height: number } } | { image: string, imageDimension: { width: number, height: number }, objectId?: string }) => {
        if (typeof file === 'object' && 'image' in file && 'imageDimension' in file) {
            const { selectedTargetImageDimension, containedTargetImageDimension } = targetImageDataList[selectedTargetIndex ?? 0];
            const uploadedScaleX = (containedTargetImageDimension.width / selectedTargetImageDimension.width) * file.imageDimension.width;
            const uploadedScaleY = (containedTargetImageDimension.height / selectedTargetImageDimension.height) * file.imageDimension.height;
            setState({
                isFileUploadModalOpen: false,
                [ObjectTypes.IMAGE]: {
                    ...state[ObjectTypes.IMAGE],
                    src: file.image,
                    objectId: file.objectId,
                    scaleX: uploadedScaleX,
                    scaleY: uploadedScaleY,
                    // What the Transform panel's Reset returns the scale to.
                    defaultScaleX: uploadedScaleX,
                    defaultScaleY: uploadedScaleY,
                }
            });
        }
    }

    const handleUploadObjectVideo = (file: { video: string, videoDimension: { width: number, height: number }, objectId?: string }) => {
        const { selectedTargetImageDimension, containedTargetImageDimension } = targetImageDataList[selectedTargetIndex ?? 0];
        const uploadedScaleX = (containedTargetImageDimension.width / selectedTargetImageDimension.width) * file.videoDimension.width;
        const uploadedScaleY = (containedTargetImageDimension.height / selectedTargetImageDimension.height) * file.videoDimension.height;
        setState({
            isFileUploadModalOpen: false,
            [ObjectTypes.VIDEO]: {
                ...state[ObjectTypes.VIDEO],
                src: file.video,
                objectId: file.objectId,
                scaleX: uploadedScaleX,
                scaleY: uploadedScaleY,
                // What the Transform panel's Reset returns the scale to.
                defaultScaleX: uploadedScaleX,
                defaultScaleY: uploadedScaleY,
            },
        })
    }

    useEffect(() => {
        stateRef.current = state;
    }, [state])

    useEffect(() => {
        if (currentSelectedPropertyType === PropertiesTypes.TARGET || currentSelectedPropertyType === PropertiesTypes.IMAGE || currentSelectedPropertyType === PropertiesTypes.VIDEO) {
            const currentTargetImage = targetImageDataList[selectedTargetIndex ?? 0];
            const currentObject = currentTargetImage?.objects.find((object) => object.id === selectedObjectID);
    
            if ((currentSelectedPropertyType === PropertiesTypes.TARGET && !currentTargetImage?.targetImage) || (currentSelectedPropertyType === PropertiesTypes.IMAGE && !currentObject?.properties.src)) handleFileUploadModalOpenImage();

            if (currentSelectedPropertyType === PropertiesTypes.VIDEO && !currentObject?.properties.src) handleFileUploadModalOpenVideo();
        }
    }, [selectedTargetImageID, selectedObjectID])

    // The effect above opens the upload dialog only when the selection changes, so once the user
    // closes it nothing brings it back for the same image-less target. The editor's empty-state
    // card asks for it again through this counter.
    useEffect(() => {
        if (!props.uploadRequestCount) return;

        const currentTargetImage = targetImageDataList[selectedTargetIndex ?? 0];
        if (currentSelectedPropertyType === PropertiesTypes.TARGET && !currentTargetImage?.targetImage) handleFileUploadModalOpenImage();
    }, [props.uploadRequestCount])

    // TEXT/BUTTON/SOCIAL objects are created in Redux asynchronously, shortly after being
    // selected (see the sync-back effect below, keyed on state.text/button/social changing) —
    // not at the moment selectedObjectID changes. So this has to re-check whenever
    // targetImageDataList changes too, not just when the selection itself changes, otherwise a
    // freshly-selected TEXT/BUTTON/SOCIAL object is never recognized as "real" before the user
    // moves on to add an Image/Video object.
    useEffect(() => {
        const currentTargetImageForSelection = targetImageDataList[selectedTargetIndex ?? 0];
        const selectedObjectAlreadyExists = currentSelectedPropertyType === PropertiesTypes.TARGET || !!currentTargetImageForSelection?.objects.find((object) => object.id === selectedObjectID);
        if (selectedObjectAlreadyExists) {
            lastValidSelectionRef.current = { objectID: selectedObjectID, type: currentSelectedPropertyType };
        }
    }, [selectedObjectID, currentSelectedPropertyType, targetImageDataList, selectedTargetIndex])

    // The Font Presets row highlights on `state.selectedFontFamily` and the Font style buttons on
    // `state.selectedFontStyle` -- top-level mirrors of the per-type fontFamily/fontStyle that only
    // the click handlers ever move. Hydration writes state[type] alone, so without this the two
    // highlights stay on whatever was last clicked while the values under them roll back.
    const fontSelectionMirrors = (properties: IObject): IObject => {
        if (currentSelectedPropertyType !== PropertiesTypes.TEXT && currentSelectedPropertyType !== PropertiesTypes.BUTTON) return {};

        return {
            selectedFontFamily: properties.fontFamily ?? FontFamilyTypes.MONTSERRAT,
            selectedFontStyle: properties.fontStyle ?? FontStyles.BOLD,
        };
    }

    // The object the form state was last loaded for. The form state is one copy per type, written back to
    // whichever object is selected, so it must be reloaded whenever the selection moves to another object.
    // Relying on every selection route to raise shouldUpdateObjectState left the routes that forget (undo,
    // delete-reselect, a canvas click) stamping the previous object's source onto the new one.
    const hydratedObjectIDRef = useRef<string | null>(null);

    useEffect(() => {
        const selectionMoved = hydratedObjectIDRef.current !== selectedObjectID;
        if (!shouldUpdateObjectState && !_isPersist && !selectionMoved) return;
        hydratedObjectIDRef.current = selectedObjectID;

        const currentTargetObjectsList: Array<TargetImageObject> = targetImageDataList[selectedTargetIndex ?? 0]?.objects || [];

        const currentObject: TargetImageObject | undefined = currentTargetObjectsList.find(
            (object) => object.id === selectedObjectID
        );

        if (currentObject) {
            const objectProperties = currentObject.properties;

            let formattedProperties: any = {};

            if (currentSelectedPropertyType === PropertiesTypes.TEXT) {
                formattedProperties = {
                    ...objectProperties,
                    fontSize: objectProperties.fontSize ?
                        { label: objectProperties.fontSize.toString(), value: objectProperties.fontSize.toString() } :
                        { label: "48", value: "48" }
                };
            } else if (currentSelectedPropertyType === PropertiesTypes.BUTTON) {
                // `objectProperties.presets` is the flat `passed` block, but the panel's preset
                // highlight reads `state.button.presets.name` -- so spreading the stored object
                // straight over the state replaces the descriptor with a nameless object and
                // un-highlights every chip. Match the stored styles back to their descriptor;
                // fall back to the default preset when the object predates the preset list.
                const matchedPreset = findButtonPresetByStyles(buttonPresets, objectProperties.presets);

                formattedProperties = {
                    ...objectProperties,
                    fontSize: objectProperties.fontSize ?
                        { label: objectProperties.fontSize.toString(), value: objectProperties.fontSize.toString() } :
                        { label: "12", value: "12" },
                    selectedPreset: objectProperties.presets,
                    presets: matchedPreset ?? getDefaultStateForObjectType(ObjectTypes.BUTTON).presets,
                };
            } else if (currentSelectedPropertyType === PropertiesTypes.SOCIAL) {
                formattedProperties = {
                    ...objectProperties,
                    assetSrc: objectProperties.src || Assets.social_yt
                };
            } else {
                formattedProperties = { ...objectProperties };
            }

            const newState = { [currentSelectedPropertyType ?? ""]: formattedProperties, ...fontSelectionMirrors(formattedProperties) }
            setState(newState);
            stateRef.current = { ...stateRef.current, ...newState };

        } else {
            const defaultState = getDefaultStateForObjectType(currentSelectedPropertyType ?? "");
            const newState = { [currentSelectedPropertyType ?? ""]: defaultState, ...fontSelectionMirrors(defaultState) }
            setState(newState);
            stateRef.current = { ...stateRef.current, ...newState };
        }

        hydratedFromStoreRef.current = true;

        if (shouldUpdateObjectState) dispatch(setShouldUpdateObjectState(false))
    }, [shouldUpdateObjectState, _isPersist, selectedObjectID]);

    useEffect(() => {
        if (isUpdatingFromEditorWorkspace || isTargetImageListItemClicked) return;
        if (!selectedObjectID || !currentSelectedPropertyType) return;

        const currentTargetObjectsList: Array<TargetImageObject> = targetImageDataList[selectedTargetIndex ?? 0]?.objects;
        const currentObject: TargetImageObject | undefined = currentTargetObjectsList.find((object) => object.id === selectedObjectID);

        // The state change that woke this effect came from the store, not from the author — see
        // hydratedFromStoreRef. For an object that already exists, hydration is a pure read and
        // must not be written back. When the object does not exist yet the hydration produced
        // default state for it, and this effect is still the path that creates it, so fall through.
        if (hydratedFromStoreRef.current) {
            hydratedFromStoreRef.current = false;
            if (currentObject) return;
        }

        let renderOrder: number | undefined;
        const currentState = stateRef.current;
        const currentStateForType = currentState[currentSelectedPropertyType ?? ""];

        if ((currentSelectedPropertyType === PropertiesTypes.IMAGE || currentSelectedPropertyType === PropertiesTypes.VIDEO) && !currentStateForType.src) return;

        if (!state.listOfAddedObjectsIDs.includes(selectedObjectID)) {
            renderOrder = (currentTargetObjectsList?.length ?? 0) + 1;
            setState({ listOfAddedObjectsIDs: [...state.listOfAddedObjectsIDs, selectedObjectID] })
        } else {
            renderOrder = currentObject?.properties.renderOrder;
        }

        if (currentSelectedPropertyType === PropertiesTypes.IMAGE) {
            const newProperties = {
                src: currentStateForType.src,
                objectId: currentStateForType.objectId,
                positionX: currentStateForType.positionX,
                positionY: currentStateForType.positionY,
                rotationX: currentStateForType.rotationX,
                scaleX: currentStateForType.scaleX,
                scaleY: currentStateForType.scaleY,
                ...(typeof currentStateForType.defaultScaleX === "number" ? { defaultScaleX: currentStateForType.defaultScaleX, defaultScaleY: currentStateForType.defaultScaleY } : {}),
                renderOrder: renderOrder,
            }
            if (currentObject) {
                const hasChanged = !compareObjectProperties(newProperties, currentObject.properties);
                if (hasChanged) {
                    dispatch(updateObjectInTargetImage({
                        targetImageID: selectedTargetImageID ?? "",
                        object: {
                            id: selectedObjectID ?? "",
                            type: ObjectTypes.IMAGE,
                            properties: newProperties
                        }
                    }))
                }
            } else {
                dispatch(addObjectsToTargetImage({
                    targetImageID: selectedTargetImageID ?? "",
                    object: {
                        id: selectedObjectID ?? "",
                        type: ObjectTypes.IMAGE,
                        properties: newProperties
                    }
                }))
            }
        } else if (currentSelectedPropertyType === PropertiesTypes.VIDEO) {
            const newProperties = {
                src: currentStateForType.src,
                objectId: currentStateForType.objectId,
                positionX: currentStateForType.positionX,
                positionY: currentStateForType.positionY,
                positionZ: currentStateForType.positionZ,
                rotationX: currentStateForType.rotationX,
                scaleX: currentStateForType.scaleX,
                scaleY: currentStateForType.scaleY,
                ...(typeof currentStateForType.defaultScaleX === "number" ? { defaultScaleX: currentStateForType.defaultScaleX, defaultScaleY: currentStateForType.defaultScaleY } : {}),
                renderOrder: renderOrder,
            }
            if (currentObject) {
                const hasChanged = !compareObjectProperties(newProperties, currentObject.properties);
                if (hasChanged) {
                    dispatch(updateObjectInTargetImage({
                        targetImageID: selectedTargetImageID ?? "",
                        object: {
                            id: selectedObjectID ?? "",
                            type: ObjectTypes.VIDEO,
                            properties: newProperties
                        }
                    }))
                }
            } else {
                dispatch(addObjectsToTargetImage({
                    targetImageID: selectedTargetImageID ?? "",
                    object: {
                        id: selectedObjectID ?? "",
                        type: ObjectTypes.VIDEO,
                        properties: newProperties
                    }
                }))
            }
        } else if (currentSelectedPropertyType === PropertiesTypes.BUTTON) {
            const newProperties = {
                presets: currentStateForType.selectedPreset,
                url: currentStateForType.url,
                text: currentStateForType.text,
                fontColor: currentStateForType.fontColor,
                fontFamily: currentStateForType.fontFamily,
                fontSize: +currentStateForType.fontSize.value,
                fontStyle: currentStateForType.fontStyle,
                bgColor: currentStateForType.bgColor,
                positionX: currentStateForType.positionX,
                positionY: currentStateForType.positionY,
                rotationX: currentStateForType.rotationX,
                scaleX: currentStateForType.scaleX,
                scaleY: currentStateForType.scaleY,
                scaleZ: currentStateForType.scaleZ,
                renderOrder: renderOrder,
            }
            if (currentObject) {
                const hasChanged = !compareObjectProperties(newProperties, currentObject.properties);
                if (hasChanged) {
                    dispatch(updateObjectInTargetImage({
                        targetImageID: selectedTargetImageID ?? "",
                        object: {
                            id: selectedObjectID ?? "",
                            type: ObjectTypes.BUTTON,
                            properties: newProperties
                        }
                    }))
                }
            } else {
                dispatch(addObjectsToTargetImage({
                    targetImageID: selectedTargetImageID ?? "",
                    object: {
                        id: selectedObjectID ?? "",
                        type: ObjectTypes.BUTTON,
                        properties: newProperties
                    }
                }))
            }
        } else if (currentSelectedPropertyType === PropertiesTypes.TEXT) {
            const newProperties = {
                text: currentStateForType.text,
                fontColor: currentStateForType.fontColor,
                fontFamily: currentStateForType.fontFamily,
                fontSize: +currentStateForType.fontSize.value,
                fontStyle: currentStateForType.fontStyle,
                bgColor: currentStateForType.bgColor,
                positionX: currentStateForType.positionX,
                positionY: currentStateForType.positionY,
                rotationX: currentStateForType.rotationX,
                scaleX: currentStateForType.scaleX,
                scaleY: currentStateForType.scaleY,
                renderOrder: renderOrder,
            }
            if (currentObject) {
                const hasChanged = !compareObjectProperties(newProperties, currentObject.properties);
                if (hasChanged) {
                    dispatch(updateObjectInTargetImage({
                        targetImageID: selectedTargetImageID ?? "",
                        object: {
                            id: selectedObjectID ?? "",
                            type: ObjectTypes.TEXT,
                            properties: newProperties
                        }
                    }))
                }
            } else {
                dispatch(addObjectsToTargetImage({
                    targetImageID: selectedTargetImageID ?? "",
                    object: {
                        id: selectedObjectID ?? "",
                        type: ObjectTypes.TEXT,
                        properties: newProperties
                    }
                }))
            }
        } else if (currentSelectedPropertyType === PropertiesTypes.SOCIAL) {
            const newProperties = {
                src: currentStateForType.assetSrc,
                color: currentStateForType.color,
                bgColor: currentStateForType.bgColor,
                url: currentStateForType.url,
                positionX: currentStateForType.positionX,
                positionY: currentStateForType.positionY,
                rotationX: currentStateForType.rotationX,
                scaleX: currentStateForType.scaleX,
                scaleY: currentStateForType.scaleY,
                renderOrder: renderOrder,
            }
            if (currentObject) {
                const hasChanged = !compareObjectProperties(newProperties, currentObject.properties);
                if (hasChanged) {
                    dispatch(updateObjectInTargetImage({
                        targetImageID: selectedTargetImageID ?? "",
                        object: {
                            id: selectedObjectID ?? "",
                            type: ObjectTypes.SOCIAL,
                            properties: newProperties
                        }
                    }))
                }
            } else {
                dispatch(addObjectsToTargetImage({
                    targetImageID: selectedTargetImageID ?? "",
                    object: {
                        id: selectedObjectID ?? "",
                        type: ObjectTypes.SOCIAL,
                        properties: newProperties
                    }
                }))
            }
        }
    }, [selectedObjectID, currentSelectedPropertyType, state.image, state.video, state.button, state.text, state.social]);

    useEffect(() => {
        if (!isUpdatingFromEditorWorkspace) return;

        const currentObject: TargetImageObject | undefined = targetImageDataList[selectedTargetIndex ?? 0]?.objects.find(
            (object) => object.id === selectedObjectID
        );

        // Always lower the flag: the canvas raises it for any object it redraws (e.g. a button's mesh
        // size), selected or not. Left up while only the target is selected, it blocks every later add.
        dispatch(setIsUpdatingFromEditorWorkspace(false));
        if (currentObject) {
            setState({
                [currentSelectedPropertyType ?? ""]: {
                    ...currentObject.properties,
                    // The ref, not this render's `state`: when a press selects an object and the canvas then
                    // reports a drag before the press's deferred effects have been rendered, `state` still
                    // holds the previously selected object's fields and would overwrite this object's.
                    ...stateRef.current[currentSelectedPropertyType ?? ""],
                    positionX: currentObject.properties.positionX || 0,
                    positionY: currentObject.properties.positionY || 0,
                    scaleX: currentObject.properties.scaleX || 0,
                    scaleY: currentObject.properties.scaleY || 0,
                    rotationX: currentObject.properties.rotationX || 0,
                    // A corner drag on text/button changes fontSize on the canvas; the panel keeps it as a
                    // {label, value} option, and its stale copy would otherwise be written back to the store.
                    ...(currentObject.properties.fontSize !== undefined && (currentSelectedPropertyType === PropertiesTypes.TEXT || currentSelectedPropertyType === PropertiesTypes.BUTTON)
                        ? { fontSize: { label: String(currentObject.properties.fontSize), value: String(currentObject.properties.fontSize) } }
                        : {}),
                },
            });
        }
    }, [isUpdatingFromEditorWorkspace]);

    // A published project, or a paid target, has no Replace to sit beside: the picture or video then shows large, as the target image does.
    const IS_CONTENT_FROZEN = props.projectStatus === ProjectStatus.PUBLISHED;
    // "Replace" is an outlined pill (the picture is already there); "Add" stays the lime call to action.
    const IS_IMAGE_REPLACEABLE = currentSelectedPropertyType === PropertiesTypes.TARGET ? IS_CURRENT_TARGET_CAN_BE_REPLACED : (!state.isFIleUploadModalOpen && !!state.image.src);
    const IS_VIDEO_REPLACEABLE = !state.isFIleUploadModalOpen && !!state[ObjectTypes.VIDEO].src;
    const REPLACE_BUTTON_STYLE = { border: `1.5px solid ${Colors.ink}`, color: Colors.ink, backgroundColor: Colors.white };

    // The header names whatever is selected: the target image, or one object by the same name the sidebar gives it.
    const propertiesStrings = STRINGS.editor.properties;
    const currentTargetObjects = targetImageDataList[selectedTargetIndex ?? 0]?.objects ?? [];
    const selectedObjectIndex = currentTargetObjects.findIndex((object) => object.id === selectedObjectID);
    const isTargetSelected = currentSelectedPropertyType === PropertiesTypes.TARGET;
    const selectionEyebrow = currentSelectedPropertyType === null ? propertiesStrings.eyebrow : (isTargetSelected ? propertiesStrings.selected : propertiesStrings.selectedObject);
    const selectedTarget = targetImageDataList[selectedTargetIndex ?? 0];
    const selectionTitle = isTargetSelected
        ? getTargetDisplayName(selectedTarget ?? {}, selectedTargetIndex ?? 0)
        : (selectedObjectIndex !== -1 ? getObjectDisplayName(currentTargetObjects, selectedObjectIndex) : getObjectTypeLabel(currentSelectedPropertyType ?? ""));
    const selectionKind = isTargetSelected ? propertiesStrings.targetTitle : getObjectTypeLabel(currentSelectedPropertyType ?? "");

    const siblingNames = isTargetSelected ? targetImageDataList.map((target, index) => getTargetDisplayName(target, index)) : currentTargetObjects.map((_object, index) => getObjectDisplayName(currentTargetObjects, index));
    const ownIndex = isTargetSelected ? (selectedTargetIndex ?? 0) : selectedObjectIndex;
    const isRenaming = state.renameDraft !== null;
    const draftName = state.renameDraft ?? selectionTitle;
    const normalizedDraft = normalizeEntityName(draftName);
    const renameError = !isRenaming ? "" : (!normalizedDraft ? propertiesStrings.renameEmpty : (isNameTaken(siblingNames, normalizedDraft, ownIndex) ? propertiesStrings.renameTaken : ""));

    const cancelRename = () => setState({ renameDraft: null });
    const saveRename = () => {
        if (!selectedTarget || !normalizedDraft || renameError) return;
        setState({ renameDraft: null });
        if (normalizedDraft === selectionTitle) return;
        if (isTargetSelected) dispatch(renameTargetImage({ targetImageID: selectedTarget.id, name: normalizedDraft }));
        else if (selectedObjectID) dispatch(renameObjectInTargetImage({ targetImageID: selectedTarget.id, objectID: selectedObjectID, name: normalizedDraft }));
    };

    // Picking something else while renaming drops the unsaved draft.
    useEffect(() => { setState({ renameDraft: null }); }, [selectedObjectID, selectedTargetIndex, currentSelectedPropertyType]);

    return (
        <div className={`properties${IS_OBJECT_SELECTED ? "" : " properties_compact"}`}>
            <div className="properties_container">
                <div className="properties_header">
                    <div className="properties_header_container">
                        <div className="properties_header_title">{selectionEyebrow}</div>
                        {
                            currentSelectedPropertyType === null ?
                                <p className="properties_header_hint">{propertiesStrings.noSelectionHint}</p> :
                                <>
                                    <div className="properties_header_selection_area">
                                    {
                                        isRenaming ?
                                            <>
                                                <RenameInput
                                                    value={draftName}
                                                    onChange={(value) => setState({ renameDraft: value })}
                                                    onSave={saveRename}
                                                    onCancel={cancelRename}
                                                    error={renameError}
                                                    maxLength={ENTITY_NAME_MAX_LENGTH}
                                                    ariaLabel={propertiesStrings.renameLabel}
                                                    saveLabel={propertiesStrings.renameSave}
                                                />
                                            </> :
                                            <>
                                                <div className="properties_header_selection">{selectionTitle}</div>
                                                <div className="properties_header_selection_kind">
                                                    <span>{selectionKind}</span>
                                                    <span className="properties_header_selection_dot" aria-hidden="true" />
                                                    <button type="button" className="properties_header_rename_link" onClick={() => setState({ renameDraft: selectionTitle })}>{propertiesStrings.renameLink}</button>
                                                </div>
                                            </>
                                    }
                                    </div>
                                </>
                        }
                    </div>
                </div>
                <div className="properties_parameters_container">
                    {
                        (currentSelectedPropertyType === PropertiesTypes.VIDEO) &&
                        <div className={`properties_video${IS_CONTENT_FROZEN ? " properties_video_stacked" : ""}`}>
                            <div className="properties_video_container">
                                {
                                    currentSelectedPropertyType === PropertiesTypes.VIDEO && (!state.isFileUploadModalOpen && state.video.src) ?
                                        <div className="properties_video_selected_container">
                                            <div className="properties_video_selected_player">
                                                <video
                                                    // autoPlay
                                                    muted
                                                    width="100%"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                    }}
                                                >
                                                    <source
                                                        src={state[ObjectTypes.VIDEO].src}
                                                        type="video/mp4"
                                                    />
                                                    Your browser does not support the video tag.
                                                </video>
                                            </div>
                                        </div> :
                                        <div className="properties_video_selected_container">
                                            <div className="properties_video_selected_placeholder">
                                                <img
                                                    src={Assets.video_placeholder}
                                                    alt="Image Placeholder"
                                                />
                                            </div>
                                        </div>
                                }
                                <div className="properties_video_button_container">
                                    <PrimaryButton
                                        onClick={handleFileUploadModalOpenVideo}
                                        type={IS_VIDEO_REPLACEABLE ? ButtonTypes.OUTLINED : ButtonTypes.PRIMARY_GRADIENT}
                                        className={IS_VIDEO_REPLACEABLE ? "properties_replace_button" : undefined}
                                        size={ButtonSize.MEDIUM}
                                        text={
                                            (!state.isFIleUploadModalOpen && state[ObjectTypes.VIDEO].src) ? "Replace Video" : "Add Video"
                                        }
                                        fontFamily={FontFamily.semiBold}
                                        lineHeight="17.407px"
                                        left_icon={
                                            (!state.isFIleUploadModalOpen && state[ObjectTypes.VIDEO].src) ? Assets.replace : Assets.plus
                                        }
                                        iconSize={{ width: 16, height: 16 }}
                                        style={{ ...(IS_VIDEO_REPLACEABLE ? REPLACE_BUTTON_STYLE : {}) }}
                                    />
                                </div>
                            </div>
                        </div>
                    }
                    {
                        (currentSelectedPropertyType === PropertiesTypes.TARGET || currentSelectedPropertyType === PropertiesTypes.IMAGE) &&
                        <div className={`properties_image${currentSelectedPropertyType === PropertiesTypes.TARGET ? " properties_image_target" : ""}${(currentSelectedPropertyType === PropertiesTypes.TARGET || IS_CONTENT_FROZEN) ? " properties_image_stacked" : ""}`}>
                            <div className="properties_image_container">
                                <div className="properties_image_selected_container">
                                    <div className="properties_image_selected_placeholder">
                                        <img
                                            src={
                                                currentSelectedPropertyType === PropertiesTypes.TARGET ? (IS_CURRENT_TARGET_CAN_BE_REPLACED ? targetImageDataList[selectedTargetIndex ?? 0]?.targetImage : Assets.image_placeholder) :
                                                    ((!state.isFileUploadModalOpen && state[ObjectTypes.IMAGE].src) ? state[ObjectTypes.IMAGE].src : Assets.image_placeholder)
                                            }
                                            alt="Image Placeholder"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: currentSelectedPropertyType === PropertiesTypes.TARGET ? 'contain' : 'cover',
                                            }}
                                        />
                                    </div>
                                </div>
                                {
                                    (props.projectStatus !== ProjectStatus.PUBLISHED) &&
                                    <div className="properties_image_button_container">
                                            <PrimaryButton
                                                onClick={handleFileUploadModalOpenImage}
                                                type={IS_IMAGE_REPLACEABLE ? ButtonTypes.OUTLINED : ButtonTypes.PRIMARY_GRADIENT}
                                                className={IS_IMAGE_REPLACEABLE ? "properties_replace_button" : undefined}
                                                size={ButtonSize.MEDIUM}
                                                text={
                                                    IS_TARGET_LOCKED ? "Image Locked" :
                                                    (currentSelectedPropertyType === PropertiesTypes.TARGET ? (IS_CURRENT_TARGET_CAN_BE_REPLACED ? "Replace Image" : "Add Image") : (!state.isFIleUploadModalOpen && state.image.src) ? "Replace Image" : "Add Image")
                                                }
                                                fontFamily={FontFamily.semiBold}
                                                lineHeight="17.407px"
                                                left_icon={
                                                    (currentSelectedPropertyType === PropertiesTypes.TARGET ? (IS_CURRENT_TARGET_CAN_BE_REPLACED ? Assets.replace : Assets.plus) : (!state.isFIleUploadModalOpen && state[ObjectTypes.IMAGE].src) ? Assets.replace : Assets.plus)
                                                }
                                                style={{ ...(IS_IMAGE_REPLACEABLE ? REPLACE_BUTTON_STYLE : {}), ...(IS_TARGET_LOCKED ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
                                                iconSize={{ width: 16, height: 16 }}
                                            />
                                    </div>
                                }
                                {
                                    currentSelectedPropertyType === PropertiesTypes.TARGET &&
                                    <p className="properties_image_hint">{propertiesStrings.targetHint}</p>
                                }
                            </div>
                        </div>
                    }
                    {
                        (currentSelectedPropertyType === PropertiesTypes.SOCIAL) && (
                            <div className="properties_social">
                                <div className="properties_social_container">
                                    <div className="properties_social_title">Icons</div>
                                    <div className="properties_social_icons_list">
                                        {socialIcons.map((icon) => (
                                            <div
                                                className="properties_social_icons_list_item"
                                                key={icon.name}
                                                onClick={() => handleSocialIconClick(icon.src, icon.color)}
                                                style={{ backgroundColor: state[ObjectTypes.SOCIAL].assetSrc === icon.src ? Colors.primaryColor : Colors.transparent }}
                                            >
                                                <img src={state[ObjectTypes.SOCIAL].assetSrc === icon.src ? icon.activeSrc : icon.src} alt={icon.name} className="properties_social_icons_list_item_img" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )
                    }
                    {
                        (currentSelectedPropertyType === PropertiesTypes.BUTTON &&
                            <div className="properties_presets_button_styles">
                                <div className="properties_presets_button_styles_container">
                                    <div className="properties_presets_button_styles_title">Button Presets</div>
                                    <div className="properties_presets_button_styles_list">
                                        {buttonPresets.map((button) => (
                                            <div className={`properties_presets_button_styles_list_item ${state.button.presets.name === button.name ? `${button.name}_active` : ""}`} key={button.name}>
                                                <PrimaryButton
                                                    text={"Label"}
                                                    fontFamily={FontFamily.medium}
                                                    size={ButtonSize.SMALL}
                                                    style={{ ...button.styles.inActive }}
                                                    onClick={() => handleButtonStyleClick(button)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )
                    }
                    {
                        (currentSelectedPropertyType === PropertiesTypes.TEXT || currentSelectedPropertyType === PropertiesTypes.BUTTON) &&
                        <div className="properties_presets_font_family">
                            <div className="properties_presets_font_family_container">
                                <div className="properties_presets_font_family_title">Font Presets</div>
                                <div className="properties_presets_font_family_list">
                                    {fontFamiliesPresets.map((font) => (
                                        <div key={font.name} className="properties_presets_font_family_list_item">
                                            <PrimaryButton
                                                text={"Text"}
                                                type={ButtonTypes.OUTLINED}
                                                size={ButtonSize.SMALL}
                                                fontFamily={font.fontFamily}
                                                onClick={() => handleFontFamilyClick(font.name)}
                                                style={{
                                                    backgroundColor: state.selectedFontFamily === font.name ? Colors.primaryColor : 'transparent',
                                                    borderColor: state.selectedFontFamily === font.name ? 'transparent' : Colors.buttonBorder,
                                                    color: state.selectedFontFamily === font.name ? Colors.white : Colors.black,
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    }
                    {
                        (currentSelectedPropertyType === PropertiesTypes.BUTTON || currentSelectedPropertyType === PropertiesTypes.SOCIAL) &&
                        <div className="properties_url_and_color">
                            <div className="properties_url_and_color_container">
                                <div className="properties_paste_url">
                                    <div className="properties_paste_url_container">
                                        <div className="properties_paste_url_title">Paste your url link</div>
                                        <div className="properties_paste_url_input_container">
                                            <Input
                                                placeholder="www.mushoorvilla.com"
                                                value={currentSelectedPropertyType === PropertiesTypes.BUTTON ? state[ObjectTypes.BUTTON].url : state.social.url}
                                                onChange={(e: any) => {
                                                    if (currentSelectedPropertyType === PropertiesTypes.BUTTON) {
                                                        setState({ [ObjectTypes.BUTTON]: { ...state[ObjectTypes.BUTTON], url: e.target.value } })
                                                    } else {
                                                        setState({ [ObjectTypes.SOCIAL]: { ...state[ObjectTypes.SOCIAL], url: e.target.value } })
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {currentSelectedPropertyType === PropertiesTypes.SOCIAL &&
                                    <div className="properties_icons_choose_color">
                                        <div className="properties_icons_choose_color_item">
                                            <div className="properties_icons_choose_color_item_title">Icon Color</div>
                                            <div className="properties_icons_choose_color_item_picker">
                                                <ColorPicker
                                                    type={ColorPickerTypes.SKETCH}
                                                    color={state.social.color}
                                                    onSelect={color => {
                                                        setState({ social: { ...state.social, color: color } })
                                                    }}
                                                    pickerStyle={{ top: "-285px", left: "0px" }}
                                                />
                                            </div>
                                        </div>
                                        <div className="properties_icons_choose_color_item">
                                            <div className="properties_icons_choose_color_item_title">bg color</div>
                                            <div className="properties_icons_choose_color_item_picker">
                                                <ColorPicker
                                                    type={ColorPickerTypes.SKETCH}
                                                    color={state.social.bgColor}
                                                    onSelect={color => {
                                                        setState({ social: { ...state.social, bgColor: color } })
                                                    }}
                                                    pickerStyle={{ top: "-285px", right: "0px" }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    }
                    {
                        (currentSelectedPropertyType === PropertiesTypes.TEXT || currentSelectedPropertyType === PropertiesTypes.BUTTON) &&
                        <div className="properties_interactions">
                            <div className="properties_interactions_container">
                                <div className="properties_interactions_title">Interaction</div>
                                <div className="properties_interactions_item properties_interactions_item_text">
                                    <div className="properties_interactions_item_title">Enter your text(30)</div>
                                    <div className="properties_interactions_item_input_container">
                                        <Input
                                            placeholder="Mushroom villa"
                                            value={state[currentSelectedPropertyType].text}
                                            onChange={(e) => {
                                                setState({ [currentSelectedPropertyType]: { ...state[currentSelectedPropertyType], text: e.target.value } })
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="properties_interactions_item_font">
                                    <div className="properties_interactions_item">
                                        <div className="properties_interactions_item_title">Font size</div>
                                        <div className="properties_interactions_item_font_size_container">
                                            <Dropdown
                                                isCreatable
                                                data={listOfFontSizes}
                                                value={state[currentSelectedPropertyType].fontSize}
                                                isValidNewOption={(inputValue: string) => {
                                                    const parsed = Number(inputValue);
                                                    return inputValue !== "" && !isNaN(parsed) && parsed >= FONT_SIZE_MIN && parsed <= FONT_SIZE_MAX;
                                                }}
                                                formatCreateLabel={(inputValue: string) => `Use ${inputValue}`}
                                                onChange={(selectedOption: any) => {
                                                    const clamped = Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, Number(selectedOption.value))).toString();
                                                    setState({ [currentSelectedPropertyType]: { ...state[currentSelectedPropertyType], fontSize: { label: clamped, value: clamped } } })
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="properties_interactions_item">
                                        <div className="properties_interactions_item_title">Font style</div>
                                        <div className="properties_interactions_item_font_style_container">
                                            {fontStyles.map((style) => {
                                                return (
                                                    <div
                                                        key={style.name} className="properties_interactions_item_font_style_button"
                                                    >
                                                        <PrimaryButton
                                                            left_icon={style.icon}
                                                            iconSize={{ width: 10, height: 10 }}
                                                            type={ButtonTypes.OUTLINED}
                                                            onClick={() => handleFontStyleClick(style.name)}
                                                            style={{
                                                                borderColor: state.selectedFontStyle === style.name ? Colors.primaryColor : Colors.border,
                                                            }}
                                                            size={ButtonSize.SMALL}
                                                        />
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                                <div className="properties_interactions_item properties_interactions_item_choose_color">
                                    <div className="properties_interactions_choose_color_font">
                                        <div className="properties_interactions_item_title">Font color</div>
                                        <div className="properties_interactions_item_color_picker_container">
                                            <ColorPicker
                                                color={currentSelectedPropertyType === PropertiesTypes.BUTTON ? state[ObjectTypes.BUTTON].fontColor : state[ObjectTypes.TEXT].fontColor}
                                                type={ColorPickerTypes.SKETCH}
                                                onSelect={(color: string) => {
                                                    setState({
                                                        [currentSelectedPropertyType]: {
                                                            ...state[currentSelectedPropertyType],
                                                            fontColor: color
                                                        }
                                                    })
                                                }}
                                                pickerStyle={{ position: "absolute", top: "-324px", left: "0px" }}
                                            />
                                        </div>
                                    </div>
                                    {currentSelectedPropertyType === PropertiesTypes.BUTTON &&
                                        <div className="properties_interactions_choose_color_bg">
                                            <div className="properties_interactions_item_title">Bg color</div>
                                            <div className="properties_interactions_item_color_picker_container">
                                                <ColorPicker
                                                    color={currentSelectedPropertyType === PropertiesTypes.BUTTON ? state[ObjectTypes.BUTTON].bgColor : state[ObjectTypes.TEXT].bgColor}
                                                    type={ColorPickerTypes.SKETCH}
                                                    onSelect={(color: string) => {
                                                        if (currentSelectedPropertyType === PropertiesTypes.BUTTON) {
                                                            setState({ [ObjectTypes.BUTTON]: { ...state[ObjectTypes.BUTTON], bgColor: color } })
                                                        } else {
                                                            setState({ [ObjectTypes.TEXT]: { ...state[ObjectTypes.TEXT], bgColor: color } })
                                                        }
                                                    }}
                                                    pickerStyle={{ position: "absolute", top: "-324px", right: "0px" }}
                                                />
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    }
                    {
                        IS_OBJECT_SELECTED &&
                        <div className="properties_controls">
                            <div className="properties_controls_container">
                                <div className="properties_controls_transform_container">
                                    <TransformProperties
                                        state={state}
                                        setState={setState}
                                        currentSelectedPropertyType={currentSelectedPropertyType ?? ""}
                                        maxScale={targetImageDataList[selectedTargetIndex ?? 0]?.selectedTargetImageDimension.width}
                                    />
                                </div>
                            </div>
                        </div>
                    }
                </div>
                <div className="properties_file_upload_container">
                    <UploadFileCard
                        type={state.fileUploadType}
                        isOpen={state.isFileUploadModalOpen}
                        onClose={() => {
                            const currentTargetImage = targetImageDataList[selectedTargetIndex ?? 0];
                            const currentObjectExists = !!currentTargetImage?.objects.find((object) => object.id === selectedObjectID);
                            const isCancellingUncreatedPlaceholder = (currentSelectedPropertyType === PropertiesTypes.IMAGE || currentSelectedPropertyType === PropertiesTypes.VIDEO) && !currentObjectExists;

                            setState({ isFileUploadModalOpen: false });

                            if (isCancellingUncreatedPlaceholder && lastValidSelectionRef.current.type !== null) {
                                dispatch(setCurrentSelectedPropertyType(lastValidSelectionRef.current.type));
                                dispatch(setSelectedObjectID(lastValidSelectionRef.current.objectID));
                            }
                        }}
                        setImages={currentSelectedPropertyType === PropertiesTypes.TARGET ? uploadTargetAndCompiledTargetImage : handleUploadObjectImage}
                        setVideo={handleUploadObjectVideo}
                        width={state.fileUploadType === UploadFileTypes.VIDEO ? '505px' : '452px'}
                        height={state.fileUploadType === UploadFileTypes.VIDEO ? '347px' : '268px'}
                        ensureTargetId={ensureTargetId}
                        existingObjectId={state.fileUploadType === UploadFileTypes.VIDEO ? state[ObjectTypes.VIDEO]?.objectId : state[ObjectTypes.IMAGE]?.objectId}
                    />
                </div>
            </div>
        </div>
    )

}

export default Properties;